import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardTitle, Field, Input, Select, useToast } from "@/core/ui";
import { useUpdateMetadata, type BacklogPuzzle } from "./api";

export function MetadataCard({ puzzle }: { puzzle: BacklogPuzzle }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const update = useUpdateMetadata(puzzle.slug);
  const [title, setTitle] = useState(puzzle.title);
  const [difficulty, setDifficulty] = useState(puzzle.difficulty);
  const [tags, setTags] = useState(puzzle.tags.join(", "));
  const [creditAuthor, setCreditAuthor] = useState(puzzle.credit_author);
  const [creditUrl, setCreditUrl] = useState(puzzle.credit_url);

  // Re-seed the form only when the server values really changed: a refetch triggered by any
  // other mutation must not wipe what the author is half-way through typing.
  const serverValues = useRef("");
  useEffect(() => {
    const snapshot = JSON.stringify([
      puzzle.title,
      puzzle.difficulty,
      puzzle.tags,
      puzzle.credit_author,
      puzzle.credit_url,
    ]);
    if (serverValues.current === snapshot) return;
    serverValues.current = snapshot;
    setTitle(puzzle.title);
    setDifficulty(puzzle.difficulty);
    setTags(puzzle.tags.join(", "));
    setCreditAuthor(puzzle.credit_author);
    setCreditUrl(puzzle.credit_url);
  }, [puzzle.title, puzzle.difficulty, puzzle.tags, puzzle.credit_author, puzzle.credit_url]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    update.mutate(
      {
        title: title.trim(),
        difficulty,
        tags: tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        credit_author: creditAuthor.trim(),
        credit_url: creditUrl.trim(),
      },
      {
        onSuccess: () => toast(t("admin.saved"), "success"),
        onError: () => toast(t("app.error_generic"), "danger"),
      },
    );
  };

  return (
    <Card>
      <CardTitle>{t("backlog.metadata")}</CardTitle>
      <form onSubmit={submit}>
        <Field label={t("backlog.title_field")} htmlFor="meta-title">
          <Input id="meta-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label={t("backlog.difficulty")} htmlFor="meta-difficulty">
          <Select
            id="meta-difficulty"
            value={String(difficulty)}
            onChange={(e) => setDifficulty(Number(e.target.value))}
          >
            {[1, 2, 3, 4, 5].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("backlog.tags")} htmlFor="meta-tags" hint={t("backlog.tags_hint")}>
          <Input id="meta-tags" value={tags} onChange={(e) => setTags(e.target.value)} />
        </Field>
        <Field
          label={t("backlog.credit_author")}
          htmlFor="meta-credit-author"
          hint={t("backlog.credit_hint")}
        >
          <Input
            id="meta-credit-author"
            value={creditAuthor}
            onChange={(e) => setCreditAuthor(e.target.value)}
          />
        </Field>
        <Field label={t("backlog.credit_url")} htmlFor="meta-credit-url">
          <Input
            id="meta-credit-url"
            type="url"
            inputMode="url"
            placeholder="https://"
            value={creditUrl}
            onChange={(e) => setCreditUrl(e.target.value)}
          />
        </Field>
        <Button type="submit" variant="secondary" loading={update.isPending}>
          {t("backlog.save_metadata")}
        </Button>
      </form>
    </Card>
  );
}
